import { runParallelAction } from "next-server-actions-parallel";

import {
    getActivityTemplates,
    getActivityTemplate,
} from "@/actions/activities/activityTemplate";
import { getActivityTemplateQueryKey } from "@/types/activities/activityTemplate";
import { getClassifications } from "@/actions/classification";
import { getQueryKey } from "@/types/classification";
import { listUsers, getUser, getCurrentUser } from "@/actions/users/user";
import { getUsersQueryKey, getUserQueryKey } from "@/types/users/user";
import { getSchoolQueryKey, getSchoolsQueryKey } from "@/types/users/school";
import { getSchool, listSchools } from "@/actions/users/school";
import { getCitiesQueryKey, getCityQueryKey } from "@/types/users/city";
import { listCities, getCity } from "@/actions/users/city";
import {
    getTeacherActivitiesQueryKey,
    getTeacherActivityQueryKey,
} from "@/types/activities/teacherActivity";
import {
    getTeacherActivities,
    getTeacherActivity,
} from "@/actions/activities/teacherActivity";
import { getActivitySettingsQueryKey } from "@/types/activities/settings";
import { getActivitySettings } from "@/actions/activities/settings";

export const QUERIES = {
    activitySettings: {
        query: {
            queryKey: getActivitySettingsQueryKey(),
            queryFn: () => runParallelAction(getActivitySettings()),
        },
        shouldPrefetch: true,
        priority: false,
    },

    skills: {
        query: {
            queryKey: getQueryKey("skills"),
            queryFn: () => runParallelAction(getClassifications("skills")),
        },
        shouldPrefetch: true,
        priority: false,
    },

    locations: {
        query: {
            queryKey: getQueryKey("locations"),
            queryFn: () => runParallelAction(getClassifications("locations")),
        },
        shouldPrefetch: true,
        priority: false,
    },

    independencyLevels: {
        query: {
            queryKey: getQueryKey("independencyLevels"),
            queryFn: () => runParallelAction(getClassifications("independencyLevels")),
        },
        shouldPrefetch: true,
        priority: false,
    },

    subjects: {
        query: {
            queryKey: getQueryKey("subjects"),
            queryFn: () => runParallelAction(getClassifications("subjects")),
        },
        shouldPrefetch: true,
        priority: false,
    },

    teacher_abilities: {
        query: {
            queryKey: getQueryKey("teacher_abilities"),
            queryFn: () => runParallelAction(getClassifications("teacher_abilities")),
        },
        shouldPrefetch: true,
        priority: false,
    },

    teacher_goals: {
        query: {
            queryKey: getQueryKey("teacher_goals"),
            queryFn: () => runParallelAction(getClassifications("teacher_goals")),
        },
        shouldPrefetch: true,
        priority: false,
    },

    classes: {
        query: {
            queryKey: getQueryKey("classes"),
            queryFn: () => runParallelAction(getClassifications("classes")),
        },
        shouldPrefetch: true,
        priority: false,
    },

    toolkit: {
        query: {
            queryKey: getQueryKey("toolkit"),
            queryFn: () => runParallelAction(getClassifications("toolkit")),
        },
        shouldPrefetch: true,
        priority: false,
    },

    activityTemplates: {
        query: {
            queryKey: getActivityTemplateQueryKey(),
            queryFn: () => runParallelAction(getActivityTemplates()),
        },
        shouldPrefetch: true,
        priority: true,
    },

    activityTemplate: {
        query: (id: number) => ({
            queryKey: getActivityTemplateQueryKey(id),
            queryFn: () => runParallelAction(getActivityTemplate(id)),
        }),
        shouldPrefetch: false,
        priority: false,
    },

    users: {
        query: {
            queryKey: getUsersQueryKey(),
            queryFn: () => runParallelAction(listUsers()),
        },
        shouldPrefetch: false,
        priority: false,
    },

    user: {
        query: (id: number) => ({
            queryKey: getUserQueryKey(id),
            queryFn: () => runParallelAction(getUser(id)),
        }),
        shouldPrefetch: false,
        priority: false,
    },

    currentUser: {
        query: {
            queryKey: getUserQueryKey(),
            queryFn: () => getCurrentUser(),
        },
        shouldPrefetch: true,
        priority: true,
    },

    teacherActivities: {
        query: {
            queryKey: getTeacherActivitiesQueryKey(),
            queryFn: () => runParallelAction(getTeacherActivities()),
        },
        shouldPrefetch: true,
        priority: true,
    },

    teacherActivitiesOtherUser: {
        query: (userId?: number) => ({
            queryKey: getTeacherActivitiesQueryKey(userId),
            queryFn: () => runParallelAction(getTeacherActivities(userId)),
        }),
        shouldPrefetch: false,
        priority: false,
    },

    teacherActivity: {
        query: (id: number) => ({
            queryKey: getTeacherActivityQueryKey(id),
            queryFn: () => runParallelAction(getTeacherActivity(id)),
        }),
        shouldPrefetch: false,
        priority: false,
    },

    schools: {
        query: {
            queryKey: getSchoolsQueryKey(),
            queryFn: () => runParallelAction(listSchools()),
        },
        shouldPrefetch: true,
        priority: true,
    },

    school: {
        query: (id: number) => ({
            queryKey: getSchoolQueryKey(id),
            queryFn: () => runParallelAction(getSchool(id)),
        }),
        shouldPrefetch: false,
        priority: false,
    },

    cities: {
        query: {
            queryKey: getCitiesQueryKey(),
            queryFn: () => runParallelAction(listCities()),
        },
        shouldPrefetch: false,
        priority: false,
    },

    city: {
        query: (id: number) => ({
            queryKey: getCityQueryKey(id),
            queryFn: () => runParallelAction(getCity(id)),
        }),
        shouldPrefetch: false,
        priority: false,
    },
} as const;
